/**
 * Order Overview Component
 * Displays upcoming services/orders in a table format
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { GeocodedLocationLink } from '@/components/shared/GeocodedLocationLink'
import { 
  Clock, 
  User, 
  MapPin, 
  CalendarDays, 
  ArrowRight,
  Inbox,
  ExternalLink,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useLocalization } from '@/contexts/LocalizationContext'

// ==================== Types ====================

interface Order {
  id: string
  customId: string
  serviceId?: string
  time: string
  service: string
  pickupLocation?: string
  pickupLocationUrl?: string
  dropOffLocation?: string
  dropOffLocationUrl?: string
  provider: string
  /** Backend service status shown as-is */
  status: string
}

interface OrderOverviewProps {
  orders: Order[]
  onViewAll?: () => void
  loading?: boolean
  title?: string
  maxItems?: number
}

// ==================== Component ====================

export function OrderOverview({ 
  orders, 
  onViewAll,
  loading = false,
  title,
  maxItems = 5,
}: OrderOverviewProps) {
  const navigate = useNavigate()
  const { t } = useLocalization()
  
  const displayedOrders = orders.slice(0, maxItems)
  const displayTitle = title || t('dashboard.upcomingServices')
  const upcomingCount = orders.filter((o) =>
    ['SCHEDULED', 'ASSIGNED', 'ACCEPTED'].includes((o.status || '').toUpperCase())
  ).length

  // Loading state
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-8 w-24" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg font-semibold">{displayTitle}</CardTitle>
            </div>
            {upcomingCount > 0 && (
              <Badge 
                className="bg-green-100 text-green-700 border-green-200 text-xs px-2 py-0.5 rounded-full"
              >
                {t('dashboard.upcoming', { count: upcomingCount })}
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onViewAll}
            className="text-primary hover:text-primary/90"
          >
            {t('dashboard.viewAll')}
            <ArrowRight className="h-4 w-4 ms-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Empty State */}
        {displayedOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Inbox className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-medium text-lg mb-1">{t('dashboard.noUpcomingServices')}</h3>
            <p className="text-sm text-muted-foreground max-w-[280px] mb-4">
              {t('dashboard.noScheduledServices')}
            </p>
            <Button variant="outline" onClick={() => navigate('/orders')}>
              {t('dashboard.browseOrders')}
            </Button>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {displayedOrders.map((order, index) => (
                <div
                  key={order.serviceId ? `${order.id}-${order.serviceId}` : `${order.id}-${index}`}
                  className="rounded-lg border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{order.service}</p>
                      <Link
                        to={`/orders/${order.id}`}
                        className="text-xs text-primary font-mono hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {order.customId || order.id.slice(0, 8)}
                      </Link>
                    </div>
                    <StatusBadge status={order.status} />
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3 flex-shrink-0" />
                      {order.time}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate max-w-[100px]">{order.provider}</span>
                    </span>
                  </div>
                  {(order.pickupLocation || order.dropOffLocation) && (
                    <div className="mt-2 flex flex-col gap-1 text-xs">
                      {order.pickupLocation && (
                        <div className="flex items-start gap-1.5 text-muted-foreground">
                          <MapPin className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
                          <GeocodedLocationLink
                            location={order.pickupLocation}
                            href={order.pickupLocationUrl}
                            wrap
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto -mx-6">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="ps-6 w-[120px]">{t('dashboard.orderId')}</TableHead>
                    <TableHead className="w-[160px]">{t('dashboard.time')}</TableHead>
                    <TableHead>{t('dashboard.serviceAndLocations')}</TableHead>
                    <TableHead className="w-[150px]">{t('dashboard.provider')}</TableHead>
                    <TableHead className="pe-6 w-[100px] text-end">{t('common.status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedOrders.map((order, index) => (
                    <TableRow
                      key={order.serviceId ? `${order.id}-${order.serviceId}` : `${order.id}-${index}`}
                      className="cursor-pointer hover:bg-muted/50 transition-colors group/row"
                      onClick={() => navigate(`/orders/${order.id}`)}
                    >
                      <TableCell className="ps-6 font-mono text-sm">
                        <Link
                          to={`/orders/${order.id}`}
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 -mx-1.5 -my-0.5',
                            'text-primary font-medium hover:underline hover:bg-primary/5',
                            'transition-colors cursor-pointer'
                          )}
                          onClick={(e) => e.stopPropagation()}
                          title="View order details"
                        >
                          {order.customId || order.id.slice(0, 8)}
                          <ExternalLink className="h-3.5 w-3.5 opacity-60 group-hover/row:opacity-100 shrink-0" />
                        </Link>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm whitespace-nowrap">{order.time}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1.5">
                          <p className="text-sm font-medium">{order.service}</p>
                          {(order.pickupLocation || order.dropOffLocation) && (
                            <div className="flex flex-col gap-1 text-xs">
                              {order.pickupLocation && (
                                <div className="flex items-start gap-1.5 text-muted-foreground">
                                  <MapPin className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
                                  <GeocodedLocationLink
                                    location={order.pickupLocation}
                                    href={order.pickupLocationUrl}
                                    wrap
                                  />
                                </div>
                              )}
                              {order.dropOffLocation && (
                                <div className="flex items-start gap-1.5 text-muted-foreground">
                                  <MapPin className="h-3 w-3 text-red-500 flex-shrink-0 mt-0.5" />
                                  <GeocodedLocationLink
                                    location={order.dropOffLocation}
                                    href={order.dropOffLocationUrl}
                                    wrap
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                            <User className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <span className="text-sm truncate max-w-[100px]">{order.provider}</span>
                        </div>
                      </TableCell>
                      <TableCell className="pe-6 text-end">
                        <StatusBadge status={order.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        
        )}

        {/* Show more indicator */}
        {orders.length > maxItems && (
          <div className="mt-4 pt-4 border-t text-center">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onViewAll}
              className="text-muted-foreground hover:text-foreground"
            >
              {t('dashboard.moreOrders', { count: orders.length - maxItems })}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ==================== Compact Version ====================

interface CompactOrderListProps {
  orders: Order[]
  onOrderClick?: (orderId: string) => void
  maxItems?: number
}

export function CompactOrderList({ 
  orders, 
  onOrderClick,
  maxItems = 5,
}: CompactOrderListProps) {
  const { t } = useLocalization()
  const displayedOrders = orders.slice(0, maxItems)

  if (displayedOrders.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Inbox className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">{t('dashboard.noOrders')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {displayedOrders.map((order) => (
        <div
          key={order.id}
          className={cn(
            'flex items-center justify-between p-3 rounded-lg border',
            'hover:bg-muted/50 transition-colors',
            onOrderClick && 'cursor-pointer'
          )}
          onClick={() => onOrderClick?.(order.id)}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0">
              <StatusBadge status={order.status} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{order.service}</p>
              <p className="text-xs text-muted-foreground truncate">
                #{order.customId} • {order.time}
              </p>
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        </div>
      ))}
    </div>
  )
}
